import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ThermalGrid, CLIP_MIN, CLIP_MAX, isValidTemp } from './ThermalGrid';

const ROWS = 24;
const COLS = 32;

const ThermalPanel: React.FC = () => {
  const thermalByDevice = useSelector((state: RootState) => state.dashboard.thermalByDevice);

  // Get the latest thermal data from any device
  const latestThermal = useMemo(() => {
    const allData: { deviceName: string; data: typeof thermalByDevice[string][number] }[] = [];
    Object.entries(thermalByDevice).forEach(([deviceName, dataArray]) => {
      if (dataArray.length > 0) {
        allData.push({ deviceName, data: dataArray[dataArray.length - 1] });
      }
    });
    if (allData.length === 0) return null;
    // Return the most recent one
    return allData.sort((a, b) => b.data.timestamp - a.data.timestamp)[0];
  }, [thermalByDevice]);

  const gridData = useMemo(() => {
    if (latestThermal?.data.gridData && latestThermal.data.gridData.length === ROWS * COLS) {
      return latestThermal.data.gridData;
    }
    return Array.from({ length: ROWS * COLS }, (_, i) => {
      const row = Math.floor(i / COLS);
      const col = i % COLS;
      const centerRow = ROWS / 2;
      const centerCol = COLS / 2;
      const dist = Math.sqrt(Math.pow(row - centerRow, 2) + Math.pow(col - centerCol, 2));
      const maxDist = Math.sqrt(Math.pow(centerRow, 2) + Math.pow(centerCol, 2));
      const baseTemp = 36.5;
      const hotspot = Math.max(0, 1 - dist / (maxDist * 0.6));
      return baseTemp + hotspot * 2.5 + (Math.random() - 0.5) * 0.5;
    });
  }, [latestThermal]);

  const validGridData = useMemo(() => gridData.filter(isValidTemp), [gridData]);
  
  const minTemp = useMemo(() => validGridData.length > 0 ? Math.min(...validGridData) : 0, [validGridData]);
  const maxTemp = useMemo(() => validGridData.length > 0 ? Math.max(...validGridData) : 0, [validGridData]);
  const avgTemp = useMemo(() => validGridData.length > 0 ? validGridData.reduce((a: number, b: number) => a + b, 0) / validGridData.length : 0, [validGridData]);
  const clippedMinTemp = useMemo(() => validGridData.length > 0 ? Math.min(...validGridData.map((t) => Math.min(CLIP_MAX, Math.max(CLIP_MIN, t)))) : CLIP_MIN, [validGridData]);
  const clippedMaxTemp = useMemo(() => validGridData.length > 0 ? Math.max(...validGridData.map((t) => Math.min(CLIP_MAX, Math.max(CLIP_MIN, t)))) : CLIP_MAX, [validGridData]);

  const tempColor = (temp: number) => {
    if (temp >= 38) return 'text-red-500';
    if (temp >= 37) return 'text-orange-500';
    if (temp >= 36) return 'text-green-500';
    return 'text-blue-500';
  };

  return (
    <div className="bg-white dark:bg-claude-darkSurface rounded-xl p-4 shadow-sm border border-gray-200 dark:border-claude-darkBorder h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-claude-darkText text-sm">Thermal</h3>
            <p className="text-xs text-gray-500 dark:text-claude-darkTextSecondary">MLX90640 24×32</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${tempColor(maxTemp)}`}>
            {maxTemp.toFixed(1)}°
          </div>
          <div className="text-xs text-gray-500 dark:text-claude-darkTextSecondary">max</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center min-h-0">
        <ThermalGrid gridData={gridData} />
      </div>

      <div className="flex justify-between mt-2 text-xs text-center">
        <div className="flex-1">
          <div className="text-blue-500 font-medium">{minTemp.toFixed(1)}°</div>
          <div className="text-gray-500 dark:text-claude-darkTextSecondary">Min</div>
        </div>
        <div className="flex-1">
          <div className="text-green-500 font-medium">{avgTemp.toFixed(1)}°</div>
          <div className="text-gray-500 dark:text-claude-darkTextSecondary">Avg</div>
        </div>
        <div className="flex-1">
          <div className="text-red-500 font-medium">{maxTemp.toFixed(1)}°</div>
          <div className="text-gray-500 dark:text-claude-darkTextSecondary">Max</div>
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 dark:text-claude-darkTextSecondary">{CLIP_MIN.toFixed(0)}C</span>
          <div
            className="flex-1 h-2 rounded-full"
            style={{
              background: 'linear-gradient(to right, rgb(100,0,0), rgb(180,20,0), rgb(255,60,0), rgb(255,100,0), rgb(255,150,0), rgb(255,200,0), rgb(255,230,0), rgb(255,240,50), rgb(255,220,120), rgb(255,255,255))',
            }}
          />
          <span className="text-xs text-gray-500 dark:text-claude-darkTextSecondary">{CLIP_MAX.toFixed(0)}C</span>
        </div>
        <div className="mt-1 text-[11px] text-gray-500 dark:text-claude-darkTextSecondary">
          Range: {CLIP_MIN.toFixed(0)}-{CLIP_MAX.toFixed(0)}C | Frame: {clippedMinTemp.toFixed(1)}-{clippedMaxTemp.toFixed(1)}C
        </div>
        <div className="mt-1 text-[10px] text-yellow-600 dark:text-yellow-400">
          Red = cold/env | Yellow = body (36.5C) | White = hot
        </div>
      </div>
    </div>
  );
};

export default ThermalPanel;
